import { Injectable, inject, OnDestroy } from '@angular/core';
import {
  ApiWebSocketService,
  AuthService,
  VendorContextService,
  ApiAuthService,
  ApiConfigService,
} from '@shared';
import { VendorPushService } from './vendor-push.service';
import { VendorOrdersService } from '../core/services/vendor-orders.service';
import { ToastController } from '@ionic/angular/standalone';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject, Subscription, merge, timer } from 'rxjs';
import { PendingOrderReminderService } from './pending-order-reminder.service';
import { VendorNativeAlarmService } from './vendor-native-alarm.service';
import { Capacitor } from '@capacitor/core';
import { resolvePaidOrderAlert } from './is-paid-order-alert.util';

/**
 * Plays a sound/alert when the vendor receives a new order.
 * Uses existing backend data only: WebSocket status updates and GET /orders/vendor/tab-counts.
 * Stops when the New tab is empty and will not ring again until another new order arrives.
 *
 * Chrome requires user gesture before Web Audio can play. We unlock on first click/touch.
 * Native: continuous ring via VendorOrderRing; while ringing, native polls tab-counts
 * so a customer cancel stops the sound even if the app is backgrounded.
 */
@Injectable({ providedIn: 'root' })
export class NewOrderAlertService implements OnDestroy {
  private readonly wsService = inject(ApiWebSocketService);
  private readonly authService = inject(AuthService);
  private readonly vendorContext = inject(VendorContextService);
  private readonly vendorPush = inject(VendorPushService);
  private readonly toastController = inject(ToastController);
  private readonly pendingOrderReminder = inject(PendingOrderReminderService);
  private readonly vendorOrdersService = inject(VendorOrdersService);
  private readonly nativeAlarm = inject(VendorNativeAlarmService);
  private readonly apiAuth = inject(ApiAuthService);
  private readonly apiConfig = inject(ApiConfigService);
  private readonly destroy$ = new Subject<void>();
  private readonly isNative = Capacitor.isNativePlatform();

  /** Active browser notifications keyed by order id (closed on dismiss). */
  private readonly activeBrowserNotifications = new Map<string, Notification>();

  private listenersSetup = false;
  private lastPlayedAt = 0;
  private lastNotificationShownAt = 0;
  private newTabWatchSub: Subscription | null = null;
  private readonly MIN_INTERVAL_MS = 500; // Avoid double-sound for same order; allow subsequent orders
  private readonly MIN_NOTIFICATION_INTERVAL_MS = 2000; // Avoid showing multiple notifications too quickly
  private readonly TOAST_DURATION_MS = 5000;

  /** AudioContext unlocked on first user gesture (required by Chrome autoplay policy) */
  private audioContext: AudioContext | null = null;

  /**
   * Call from app init to start listening for new orders.
   */
  start(): void {
    this.setupAudioUnlock();
    this.setupListeners();
    this.nativeAlarm.syncWatch(this.apiConfig.getApiUrl(), this.apiAuth.getToken());
  }

  /**
   * Play test sound. Call from user click (e.g. "Test sound" button) to unlock and verify.
   */
  playTestSound(): void {
    this.unlockAndPrime();
    this.playAlert();
  }

  /**
   * Chrome requires AudioContext to be created AND a sound played during user gesture.
   * We play a silent buffer on first click to unlock; then real sounds work later.
   */
  private setupAudioUnlock(): void {
    if (typeof document === 'undefined') return;
    const events = ['click', 'touchstart', 'keydown'];
    const doUnlock = () => {
      this.unlockAndPrime();
      events.forEach((e) => document.removeEventListener(e, doUnlock));
    };
    events.forEach((e) => document.addEventListener(e, doUnlock, { once: true, passive: true }));
  }

  private unlockAndPrime(): void {
    if (this.audioContext?.state === 'running') return;
    try {
      const Ctx = window.AudioContext || (window as any).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      this.audioContext = ctx;
      if (ctx.state === 'suspended') {
        void ctx.resume();
      }
      // Play a silent buffer during user gesture - Chrome requires actual playback to unlock
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.01, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(ctx.currentTime);
    } catch {
      // ignore
    }
  }

  private setupListeners(): void {
    if (this.listenersSetup) return;
    this.listenersSetup = true;

    const vendorFilter = (data: unknown) => this.isRelevantToCurrentVendor(data);

    const newOrder$ = this.wsService.onNewOrder.pipe(filter(vendorFilter));

    const orderStatusNew$ = this.wsService.onOrderStatusUpdate.pipe(
      filter((data) => data?.status === 'pending'),
      filter(vendorFilter),
    );

    const orderLeftNewTab$ = this.wsService.onOrderStatusUpdate.pipe(
      filter(vendorFilter),
      filter((data) => this.isCancelledOrLeftNewTab(data)),
    );

    // Listen to all FCM foreground messages (not just new-order)
    // This ensures we catch all push notifications
    const fcmNewOrder$ = this.vendorPush.onForegroundMessage().pipe(
      filter((p) => {
        if (!this.isAuthenticatedVendor()) {
          return false;
        }
        // When FCM payload includes vendor identity, require a match; otherwise allow
        // (token was registered for this logged-in vendor).
        if (this.payloadHasVendorIdentity(p.data ?? p) && !vendorFilter(p.data ?? p)) {
          return false;
        }
        const type = p.data?.['type'] || p.data?.['notificationType'];
        // Handle new order notifications
        if (type === 'new-order' || type === 'new_order') {
          return true;
        }
        // Server-side pending-order reminders (app killed / background)
        if (type === 'order-reminder') {
          return true;
        }
        // Also handle order status updates that might come via push
        if (type === 'order_status_update' && p.data?.['status'] === 'pending') {
          return true;
        }
        return false;
      }),
    );

    merge(newOrder$, orderStatusNew$, fcmNewOrder$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        void this.handleOrderAlert(data);
      });

    orderLeftNewTab$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        void this.silenceIfNewTabEmpty();
      });
  }

  private async handleOrderAlert(data: any): Promise<void> {
    if (!this.isAuthenticatedVendor()) {
      return;
    }
    // POS walk-in sales are completed at the counter — never ring/alert vendors for them.
    if (this.isPosOrderPayload(data)) {
      return;
    }
    if (this.isCancelledOrLeftNewTab(data)) {
      await this.silenceIfNewTabEmpty();
      return;
    }

    const paidKind = resolvePaidOrderAlert(data);
    if (paidKind === 'unpaid') {
      return;
    }

    const type =
      data?.data?.['type'] ||
      data?.data?.['notificationType'] ||
      data?.type ||
      data?.notificationType;
    const isReminder = type === 'order-reminder';

    // Stamp cooldown before any await / early return so the 10s reminder
    // timer cannot start audio first.
    if (!isReminder) {
      this.pendingOrderReminder.markFanfarePlayed();
    }

    const now = Date.now();
    if (now - this.lastPlayedAt < this.MIN_INTERVAL_MS) {
      return;
    }

    let counts = await this.vendorOrdersService.refreshTabCounts(true);
    let seeded = false;
    if (counts.new <= 0) {
      // Only a confirmed-paid new-order may seed while tab-counts lag.
      // Reminders and unknown/unpaid payloads must not chirp then stop.
      if (isReminder || paidKind !== 'paid') {
        await this.silenceUntilNewOrders();
        return;
      }
      this.vendorOrdersService.seedPendingNewCountIfEmpty();
      counts = { ...counts, new: Math.max(1, counts.new) };
      seeded = true;
    }

    if (counts.new <= 0) {
      await this.nativeAlarm.setContinueAllowed(false);
      return;
    }

    if (isReminder && (this.nativeAlarm.isRinging || (await this.nativeAlarm.isRingPlaying()))) {
      return;
    }

    this.lastPlayedAt = now;
    console.log(`[NewOrderAlert] ${isReminder ? 'Order reminder' : 'New order'} received`, data);
    if (isReminder) {
      this.nativeAlarm.playPendingReminder(1, this.ringWatchCreds());
    } else {
      this.playAlert(seeded);
    }
    this.ensureNewTabWatch();
    void this.showNotification(data);
  }

  private isAuthenticatedVendor(): boolean {
    const profile = this.authService.profile();
    return !!profile?.id && profile.role === 'vendor';
  }

  private payloadHasVendorIdentity(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }
    if (data.vendorId || data['vendorId']) {
      return true;
    }
    const order = data.order ?? data.data;
    return Array.isArray(order?.vendors) && order.vendors.length > 0;
  }

  /** Stop looping ring/reminders when the New tab is empty. */
  private async silenceIfNewTabEmpty(): Promise<void> {
    const counts = await this.vendorOrdersService.refreshTabCounts(true);
    if (counts.new <= 0) {
      await this.silenceUntilNewOrders();
    }
  }

  private async silenceUntilNewOrders(): Promise<void> {
    this.stopNewTabWatch();
    this.nativeAlarm.stopAllSounds();
    await this.pendingOrderReminder.stopAllReminders();
  }

  private ringWatchCreds(): { apiUrl: string; token: string } | undefined {
    const token = this.apiAuth.getToken();
    const apiUrl = this.apiConfig.getApiUrl();
    if (!token || !apiUrl) {
      return undefined;
    }
    return { apiUrl, token };
  }

  /** While ringing, re-check existing tab-counts so a cancel stops the sound without a stop-push. */
  private ensureNewTabWatch(): void {
    if (this.newTabWatchSub) {
      return;
    }
    this.newTabWatchSub = timer(3000, 4000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => void this.silenceIfNewTabEmpty());
  }

  private stopNewTabWatch(): void {
    this.newTabWatchSub?.unsubscribe();
    this.newTabWatchSub = null;
  }

  private isCancelledOrLeftNewTab(data: any): boolean {
    const status =
      data?.status ??
      data?.data?.status ??
      data?.order?.status ??
      data?.data?.['status'] ??
      data?.data?.order?.status;
    if (status === 'cancelled' || status === 'delivered') {
      return true;
    }
    const cancelledAt =
      data?.cancelledAt ?? data?.data?.cancelledAt ?? data?.order?.cancelledAt ?? data?.data?.order?.cancelledAt;
    if (cancelledAt) {
      return true;
    }
    const vendorStatus =
      data?.vendorStatus ?? data?.data?.vendorStatus ?? data?.order?.vendorStatus ?? data?.data?.order?.vendorStatus;
    return vendorStatus === 'cancelled' || vendorStatus === 'accepted';
  }

  /** True when the WS/FCM payload belongs to the logged-in vendor (matches VendorOrdersService). */
  private isRelevantToCurrentVendor(data: any): boolean {
    if (!this.isAuthenticatedVendor()) {
      return false;
    }
    const vid = this.vendorContext.vendor()?.id ?? this.vendorContext.vendorId();
    if (!vid) {
      return false;
    }
    if (data?.vendorId === vid) {
      return true;
    }
    const order = data?.order ?? data?.data;
    const vendors = order?.vendors;
    if (Array.isArray(vendors)) {
      return vendors.some((v: any) => v.id === vid || v.vendorId === vid);
    }
    return false;
  }

  /** POS counter orders (have posLocationId) must not trigger kitchen alerts. */
  private isPosOrderPayload(data: any): boolean {
    if (data?.posLocationId || data?.order?.posLocationId) {
      return true;
    }
    const nested = data?.data;
    if (nested && typeof nested === 'object') {
      if (nested.posLocationId || nested.order?.posLocationId) {
        return true;
      }
      // FCM data values are often strings
      const flag = nested['posLocationId'] ?? nested['isPos'];
      if (typeof flag === 'string' && flag.trim() && flag !== 'false' && flag !== '0') {
        return true;
      }
    }
    return false;
  }

  /**
   * Start continuous phone-style ring until user accepts/dismisses orders.
   * Further clips only continue while New-tab unanswered count > 0.
   */
  private playAlert(seedPending = false): void {
    this.nativeAlarm.startContinuousRing(1, { seedPending, ...this.ringWatchCreds() });
  }

  /**
   * Show in-app notification for new order (web browser notification + Ionic toast)
   * Handles data from WebSocket, FCM push notifications, and order status updates
   */
  private async showNotification(data: any): Promise<void> {
    const now = Date.now();
    if (now - this.lastNotificationShownAt < this.MIN_NOTIFICATION_INTERVAL_MS) {
      return; // Throttle notifications
    }
    this.lastNotificationShownAt = now;

    // Extract order information from various data formats
    // Handle WebSocket format
    let orderId = data?.orderId || data?.id;
    let orderNumber = data?.orderNumber || data?.order?.orderNumber;
    
    // Handle FCM push notification format (data.payload or data.data)
    const payload = data?.payload || data;
    if (payload?.data) {
      orderId = orderId || payload.data.orderId || payload.data.id;
      orderNumber = orderNumber || payload.data.orderNumber;
    }
    
    // Handle direct FCM data format
    if (data?.data && typeof data.data === 'object') {
      orderId = orderId || data.data.orderId || data.data.id;
      orderNumber = orderNumber || data.data.orderNumber;
    }
    
    // Handle notification payload format (from FCM notification.title/body)
    if (data?.notification) {
      const notificationTitle = data.notification.title || '';
      const notificationBody = data.notification.body || '';
      
      // Use notification title/body if we don't have order details
      if (!orderNumber && notificationTitle) {
        // Try to extract order number from title (e.g., "New Order #12345" or "Order #ORD123")
        const orderMatch = notificationTitle.match(/#(\w+)/) || notificationTitle.match(/Order\s+(\w+)/i);
        if (orderMatch) {
          orderNumber = `#${orderMatch[1]}`;
        } else if (notificationTitle.includes('New Order')) {
          // Fallback: use part of title
          orderNumber = notificationTitle.replace('New Order', '').trim() || undefined;
        }
      }
      
      // If we have notification title/body but no extracted data, use them directly
      if (notificationTitle && !orderNumber) {
        // Use notification title as-is if it contains order info
        const title = notificationTitle;
        const body = notificationBody || `New order received`;
        this.displayNotifications(title, body, orderId);
        return;
      }
    }
    
    // Fallback values
    orderNumber = orderNumber || `#${orderId?.substring(0, 8) || 'NEW'}`;
    
    const title = `Order #${orderNumber}`;
    const body = 'New order received';
    
    this.displayNotifications(title, body, orderId);
  }

  /**
   * Display browser notification and Ionic toast
   */
  private async displayNotifications(title: string, body: string, orderId?: string): Promise<void> {
    const notificationKey = orderId ?? `order-${Date.now()}`;

    // Native: continuous ring already posts an FGS notification + sound.
    // A second LocalNotifications.schedule with channel sound overlaps when the app is open.
    // Keep toast below for in-app UX; tray/sound come from VendorOrderRing / FCM when backgrounded.

    // Show browser notification (web) — stays until user dismisses; pauses 10s reminder loop on close.
    if (!this.isNative && typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      try {
        const existing = this.activeBrowserNotifications.get(notificationKey);
        existing?.close();

        const notificationOptions: NotificationOptions & { vibrate?: number[] } = {
          body,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: notificationKey,
          requireInteraction: true,
          silent: false,
          vibrate: [200, 100, 200],
          data: { orderId, type: 'new-order' },
        };
        const notification = new Notification(title, notificationOptions);
        this.activeBrowserNotifications.set(notificationKey, notification);

        const onCleared = () => {
          this.activeBrowserNotifications.delete(notificationKey);
          this.pendingOrderReminder.pauseReminders();
        };

        notification.onclose = onCleared;
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          this.vendorPush.handleNotificationTap({ orderId, type: 'new-order' });
          notification.close();
        };
      } catch (error) {
        console.warn('[NewOrderAlert] Browser notification failed:', error);
      }
    }

    // Toast auto-dismisses after 5s; explicit Dismiss/View still pauses the 10s reminder loop.
    try {
      const toast = await this.toastController.create({
        message: `${title} - ${body}`,
        duration: this.TOAST_DURATION_MS,
        position: 'top',
        color: 'success',
        buttons: [
          {
            text: 'View',
            role: 'button',
            handler: () => {
              this.vendorPush.handleNotificationTap({ orderId, type: 'new-order' });
              this.pendingOrderReminder.pauseReminders();
              this.closeBrowserNotification(notificationKey);
            },
          },
          {
            text: 'Dismiss',
            role: 'cancel',
            handler: () => {
              this.pendingOrderReminder.pauseReminders();
              this.closeBrowserNotification(notificationKey);
            },
          },
        ],
        cssClass: 'new-order-toast',
      });

      await toast.present();
    } catch (error) {
      console.error('[NewOrderAlert] Toast notification failed:', error);
    }
  }

  private closeBrowserNotification(key: string): void {
    const n = this.activeBrowserNotifications.get(key);
    if (n) {
      n.onclose = null;
      n.close();
      this.activeBrowserNotifications.delete(key);
    }
  }

  ngOnDestroy(): void {
    this.stopNewTabWatch();
    this.destroy$.next();
    this.destroy$.complete();
    for (const n of this.activeBrowserNotifications.values()) {
      n.close();
    }
    this.activeBrowserNotifications.clear();
    if (this.audioContext?.state !== 'closed') {
      this.audioContext?.close();
    }
  }
}
